<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { XMarkIcon, MapPinIcon } from '@heroicons/vue/24/outline';

interface Props {
  isOpen: boolean;
  currentCity: string;
  isSaving?: boolean;
}

interface Emits {
  (e: 'close'): void;
  (e: 'save', city: string): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const city = ref('');
const errorMessage = ref<string | null>(null);

const MIN_LENGTH = 2;
const MAX_LENGTH = 100;

watch(() => props.isOpen, (newVal) => {
  if (newVal) {
    city.value = props.currentCity;
    errorMessage.value = null;
  }
});

watch(city, () => {
  errorMessage.value = null;
});

const validateCity = (): string | null => {
  const trimmedCity = city.value.trim();
  
  if (trimmedCity.length === 0) {
    return 'City/Municipality is required';
  }
  
  if (trimmedCity.length < MIN_LENGTH) {
    return `City/Municipality must be at least ${MIN_LENGTH} characters`;
  }
  
  if (trimmedCity.length > MAX_LENGTH) {
    return `City/Municipality must not exceed ${MAX_LENGTH} characters`;
  }
  
  return null;
};

const isValid = computed(() => {
  return city.value.trim().length >= MIN_LENGTH && city.value.trim().length <= MAX_LENGTH;
});

const handleSave = () => {
  const error = validateCity();
  
  if (error) {
    errorMessage.value = error;
    return;
  }
  
  emit('save', city.value.trim());
};

const handleClose = () => {
  emit('close');
};
</script>

<template>
  <Transition name="modal">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 z-[9999]"
      @click.self="handleClose"
    >
      <div
        class="bg-white rounded-2xl my-4 shadow-[4px_4px_0_0_rgba(0,0,0,1)] border-2 border-black max-w-md w-full transform transition-all"
        @click.stop
      >
        <!-- Header -->
        <div class="flex items-center justify-between px-6 pt-4">
          <h2 class="text-2xl font-bold text-gray-900">Edit Location</h2>
          <button
            @click="handleClose"
            class="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <XMarkIcon class="w-6 h-6" />
          </button>
        </div>

        <!-- Content -->
        <div class="p-6">
          <!-- City Input -->
          <div class="mb-2">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              City/Municipality
            </label>
            <input
              v-model="city"
              type="text"
              placeholder="e.g. Manila, Cebu City, Davao City, Bacolod, etc."
              :maxlength="MAX_LENGTH"
              :class="[
                'w-full px-4 py-3 border-2 rounded-lg focus:ring-2 transition-all',
                errorMessage 
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                  : 'border-gray-300 focus:border-blue-500 focus:ring-blue-200'
              ]"
            />
          </div>
          
          <!-- Character Count -->
          <div class="flex justify-end text-xs text-gray-500 mb-2">
            <span :class="city.length > MAX_LENGTH - 10 ? 'text-orange-600 font-semibold' : ''">
              {{ city.length }} / {{ MAX_LENGTH }}
            </span>
          </div>

          <!-- Error Message -->
          <div v-if="errorMessage" class="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <svg class="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
            </svg>
            <p class="text-sm text-red-700">{{ errorMessage }}</p>
          </div>
        </div>

        <!-- Footer -->
        <div class="flex items-center justify-end gap-3 p-6">
          <button
            @click="handleClose"
            class="px-6 py-2.5 text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            @click="handleSave"
            :disabled="!isValid || isSaving"
            class="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium shadow-md flex items-center gap-2"
          >
            <svg v-if="isSaving" class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>{{ isSaving ? 'Saving...' : 'Save Changes' }}</span>
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .bg-white,
.modal-leave-active .bg-white {
  transition: transform 0.3s ease;
}

.modal-enter-from .bg-white,
.modal-leave-to .bg-white {
  transform: scale(0.9);
}
</style>

